import { Menu, Text } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { IconUserCheck, IconUserOff, IconUserX } from "@tabler/icons-react"
import { AreYouSureModal } from "@tfg/ui"

import { useAnonymizeUser } from "~/gen/api"
import { useToggleUserActiveStatusMutation } from "~/pages/Admin/Organizations/hooks/useToggleUserStatusHook"
import { showAxiosError } from "~/utils/showAxiosError"
import { showSuccessNotification } from "~/utils/showSuccessNotification"

import {
  invalidateOrganizationQueries,
  USER_OPTIONS_NOTIFICATIONS,
  UserOptionsMenu,
  type UserOptionsUser,
} from "./user-options.shared"

type BrandUserOptionsProps = {
  readonly organization: { readonly id: string }
  readonly user: UserOptionsUser
}

export const BrandUserOptions = ({
  organization,
  user,
}: BrandUserOptionsProps) => {
  const { mutateAsync: toggleUserStatus } = useToggleUserActiveStatusMutation()
  const { mutate: anonymizeUser } = useAnonymizeUser({
    mutation: {
      onSuccess: () => {
        showSuccessNotification({
          message: USER_OPTIONS_NOTIFICATIONS.ANONYMIZE_SUCCESS.message,
        })
        invalidateOrganizationQueries(organization.id)
        return true
      },
      onError: (error: unknown) => {
        showAxiosError({
          error: error as Error,
          notificationTitle: USER_OPTIONS_NOTIFICATIONS.ANONYMIZE_ERROR.title,
          notificationMessage:
            USER_OPTIONS_NOTIFICATIONS.ANONYMIZE_ERROR.message,
          showNotification: true,
        })
      },
    },
  })

  const [anonymizeOpened, { open: openAnonymize, close: closeAnonymize }] =
    useDisclosure(false)
  const [toggleOpened, { open: openToggle, close: closeToggle }] =
    useDisclosure(false)

  const handleConfirmToggle = async (): Promise<void> => {
    await toggleUserStatus({
      data: {
        active: !user.active,
        name: user.name,
        email: user.email,
        organizationId: organization.id,
        preferedLangageIso6391: user.locale,
      },
      id: user.id,
    })
    invalidateOrganizationQueries(organization.id)
    closeToggle()
  }

  const toggleStatusLabel = user.active ? "Deactivate user" : "Activate user"
  const toggleStatusIcon = user.active ? (
    <IconUserOff size={16} />
  ) : (
    <IconUserCheck size={16} />
  )

  return (
    <>
      <AreYouSureModal
        opened={anonymizeOpened}
        onClose={closeAnonymize}
        onConfirm={() => anonymizeUser({ id: user.id })}
        areYouSureText={`Are you sure you want to anonymize ${user.name}?`}
        confirmText="Anonymize"
      />
      <AreYouSureModal
        opened={toggleOpened}
        onClose={closeToggle}
        onConfirm={handleConfirmToggle}
        areYouSureText={`Are you sure you want to ${user.active ? "deactivate" : "activate"} ${user.name}?`}
        confirmText={user.active ? "Deactivate" : "Activate"}
      />
      <UserOptionsMenu userName={user.name}>
        <Menu.Item
          leftSection={toggleStatusIcon}
          onClick={openToggle}
          data-user-email={user.email}
          data-testid={user.active ? "deactivate-user" : "activate-user"}
        >
          <Text>{toggleStatusLabel}</Text>
        </Menu.Item>
        <Menu.Item
          leftSection={<IconUserX size={16} />}
          onClick={openAnonymize}
          data-user-email={user.email}
          data-testid="anonymize-user"
        >
          <Text>Anonymize</Text>
        </Menu.Item>
      </UserOptionsMenu>
    </>
  )
}
